"use client";
import React, { useState, useEffect } from "react";
import {
  Edit,
  Trash2,
  X,
  Search,
  Plus,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { useBonusTypes } from "../../context/BonusTypeContext";
import { useAuth } from "../../context/AuthContext";
// ─── DEFAULT MOCK DATA ────────────────────────────────────────
const DEFAULT_BONUS_TYPES = [
  { id: "bt-001", name: "Festival", status: "active" },
  { id: "bt-002", name: "Performance", status: "active" },
  { id: "bt-003", name: "Annual", status: "active" },
  { id: "bt-004", name: "Referral", status: "inactive" },
  { id: "bt-005", name: "Retention", status: "active" },
  { id: "bt-006", name: "Spot Award", status: "active" },
  { id: "bt-007", name: "Overtime", status: "inactive" },
  { id: "bt-008", name: "Joining Bonus", status: "active" },
];
 
// ─── STATUS COLOR MAP ─────────────────────────────────────────
const statusColors = {
  active: "bg-green-100 text-green-700",
  inactive: "bg-red-100 text-red-700",
};
 
// ─── LOCAL HELPERS ────────────────────────────────────────────
function generateId() {
  return (
    "bt-" +
    Date.now().toString(36) +
    Math.random().toString(36).slice(2, 6)
  );
}
 
// ═══════════════════════════════════════════════════════════════
export default function BonusTypes() {
  const {
  bonusTypes,
  loading,
  error,
  fetchBonusTypes,
  createBonusType,
  updateBonusType,
  updateBonusTypeStatus,
  deleteBonusType,
} = useBonusTypes();
  const { canDo } = useAuth();

  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
 
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
 
  const [selectedBonus, setSelectedBonus] = useState(null);
 
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
 
  const [formLoading, setFormLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    status: "active",
  });
 
  // ── FILTER ────────────────────────────────────
  const filteredBonusTypes = bonusTypes.filter((b) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = b.name?.toLowerCase().includes(term);
    const matchesFilter =
      filterStatus === "All" ||
      b.status?.toLowerCase() === filterStatus.toLowerCase();
    return matchesSearch && matchesFilter;
  });
 
  const totalPages = Math.ceil(filteredBonusTypes.length / rowsPerPage) || 1;
 
  const paginatedBonusTypes = filteredBonusTypes.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );
 
  useEffect(() => {
    setPage(1);
  }, [searchTerm, filterStatus]);
 
  const getUserId = () => {
    const user = JSON.parse(localStorage.getItem("user"));
    return user?.id;
    };

  // ── STATUS TOGGLE (INSTANT) ───────────────────
const handleStatusToggle = async (bonus) => {
  const newStatus = bonus.status === "active" ? "inactive" : "active";

  const res = await updateBonusTypeStatus(bonus.id, newStatus, getUserId());

  if (!res.success) {
    alert(res.message);
  }
};
 
  // ── CREATE ────────────────────────────────────
const handleCreate = async (e) => {
  e.preventDefault();
  setFormLoading(true);

  const payload = {
    name: formData.name,
    status: formData.status,
    create_audit_id: getUserId(),
  };

  const res = await createBonusType(payload);

  if (res.success) {
    setShowCreateModal(false);
    setFormData({ name: "", status: "active" });
  } else {
    alert(res.message);
  }

  setFormLoading(false);
};
 
  // ── UPDATE ────────────────────────────────────
const handleEdit = async (e) => {
  e.preventDefault();
  setFormLoading(true);

  const payload = {
    name: formData.name,
    status: formData.status,
    update_audit_id: getUserId(),
  };

  const res = await updateBonusType(selectedBonus.id, payload);

  if (res.success) {
    setShowEditModal(false);
  } else {
    alert(res.message);
  }

  setFormLoading(false);
};
 
  // ── DELETE ────────────────────────────────────
const handleDelete = async () => {
  if (!selectedBonus?.id) return;

  setFormLoading(true);

  const res = await deleteBonusType(selectedBonus.id, getUserId());

  if (res.success) {
    setShowDeleteModal(false);
    setSelectedBonus(null);
  } else {
    alert(res.message);
  }

  setFormLoading(false);
};
 
  // ── UI ────────────────────────────────────────
  return (
    <div className="bg-sky-50 min-h-screen">
      <div className="px-6 py-6 sm:px-9 max-sm:px-4">
        {/* TITLE */}
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-5">
          Bonus Types
        </h1>
 
        {/* FILTER BAR */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-5 gap-3 bg-white rounded-xl px-3 py-3 shadow-sm">
          <div className="flex flex-wrap items-center gap-3 w-full">
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setPage(1);
              }}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition"
            >
              <option>All</option>
              <option>Active</option>
              <option>Inactive</option>
            </select>
 
            <div className="relative flex-1 min-w-[210px]">
              <Search className="absolute left-2.5 top-2.5 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search bonus types..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                className="border border-gray-300 rounded-lg px-3 py-2 pl-9 text-sm w-full bg-white outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition"
              />
            </div>

            {(searchTerm || filterStatus !== "All") && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  setFilterStatus("All");
                  setPage(1);
                }}
                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition whitespace-nowrap shrink-0 w-full sm:w-auto"
              >
                <X size={14} />
                Clear Filters
              </button>
            )}
          </div>

          {canDo("BonusTypes", "Add") && (
            <button
              onClick={() => {
                setFormData({ name: "", status: "active" });
                setShowCreateModal(true);
              }}
              className="text-white px-10 py-2 rounded-lg text-sm font-medium transition w-full sm:w-auto whitespace-nowrap"
              style={{
                background: "linear-gradient(135deg, #2563eb, #d12465)",
              }}
            >
              <Plus size={14} className="inline mr-1" />
              Create
            </button>
          )}
        </div>
 
        {/* TABLE */}
        <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-sky-100 text-gray-700">
                  <th className="py-3 px-4 text-left">ID</th>
                  <th className="py-3 px-4 text-left">Name</th>
                  <th className="py-3 px-4 text-left">Status</th>
                  {(canDo("BonusTypes", "Edit") || canDo("BonusTypes", "Delete")) && (
                  <th className="py-3 px-4 text-left">Actions</th>
                  )}
                </tr>
              </thead>
 
              <tbody>
                {paginatedBonusTypes.length > 0 ? (
                  paginatedBonusTypes.map((b, i) => (
                    <tr
                      key={b.id}
                      className={`border-b hover:bg-gray-50 transition ${
                        i % 2 === 1 ? "bg-[#f7f6fe]" : ""
                      }`}
                    >
                      <td className="py-3 px-4 text-indigo-600">
                        {(page - 1) * rowsPerPage + i + 1}
                      </td>
                      <td className="py-3 px-4 text-gray-800">
                        {b.name}
                      </td>
 
                      {/* ── CLICKABLE STATUS TOGGLE ── */}
                      <td className="py-3 px-4">
                        <span
                          onClick={() => handleStatusToggle(b)}
                          className={`
                            px-3 py-1 rounded-full text-xs font-semibold capitalize
                            cursor-pointer transition-all duration-200
                            ${statusColors[b.status]}
                            hover:scale-105 hover:shadow-md
                          `}
                        >
                          {b.status}
                        </span>
                      </td>
 
                      {(canDo("BonusTypes", "Edit") || canDo("BonusTypes", "Delete")) && (
                        <td className="py-3 px-4 flex gap-3 text-gray-600">
                          {canDo("BonusTypes", "Edit") && (
                            <Edit
                              size={16}
                              className="cursor-pointer hover:text-indigo-600"
                              onClick={() => {
                                setSelectedBonus(b);
                                setFormData({
                                  name: b.name,
                                  status: b.status,
                                });
                                setShowEditModal(true);
                              }}
                            />
                          )}

                          {canDo("BonusTypes", "Delete") && (
                            <Trash2
                              size={16}
                              className="cursor-pointer hover:text-red-600"
                              onClick={() => {
                                setSelectedBonus(b);
                                setShowDeleteModal(true);
                              }}
                            />
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="4"
                      className="text-center py-6 text-gray-500"
                    >
                      No bonus types found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
 
          {/* ── PAGINATION ── */}
          <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-3">
            <div className="flex items-center space-x-2">
              <label className="text-gray-700 text-sm">Rows:</label>
              <select
                className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition"
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setPage(1);
                }}
              >
                {[3, 5, 10, 20].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
 
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setPage(1)}
                disabled={page === 1}
                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronsLeft size={20} />
              </button>
 
              <button
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                disabled={page === 1}
                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <MdKeyboardArrowLeft size={20} />
              </button>
 
              {(() => {
                const pages = [];
                let startPage = Math.max(page - 1, 1);
                let endPage = Math.min(startPage + 2, totalPages);
                if (endPage - startPage < 2) {
                  startPage = Math.max(endPage - 2, 1);
                }
                for (let i = startPage; i <= endPage; i++) {
                  pages.push(
                    <button
                      key={i}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        i === page
                          ? "text-white bg-[linear-gradient(135deg,#2563eb,#d12465)]"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                      onClick={() => setPage(i)}
                    >
                      {i}
                    </button>
                  );
                }
                return pages;
              })()}
 
              <button
                onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                disabled={page === totalPages}
                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <MdKeyboardArrowRight size={20} />
              </button>
 
              <button
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages}
                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronsRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
 
      {/* ───────── MODALS ───────── */}
 
      {/* CREATE MODAL */}
      {showCreateModal && (
        <Modal
          title="Add Bonus Type"
          onClose={() => setShowCreateModal(false)}
        >
          <form onSubmit={handleCreate} className="space-y-4">
            <Input
              label="Name"
              value={formData.name}
              placeholder="e.g. Festival, Performance, Annual..."
              required
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
            />
            <Status formData={formData} setFormData={setFormData} />
            <Actions onClose={() => setShowCreateModal(false)} loading={formLoading} />
          </form>
        </Modal>
      )}
 
      {/* EDIT MODAL */}
      {showEditModal && (
        <Modal
          title="Edit Bonus Type"
          onClose={() => setShowEditModal(false)}
        >
          <form onSubmit={handleEdit} className="space-y-4">
            <Input
              label="Name"
              value={formData.name}
              required
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
            />
            <Status formData={formData} setFormData={setFormData} />
            <Actions onClose={() => setShowEditModal(false)} loading={formLoading} />
          </form>
        </Modal>
      )}
 
      {/* DELETE MODAL */}
      {showDeleteModal && (
        <Modal
          title="Delete Bonus Type"
          onClose={() => setShowDeleteModal(false)}
        >
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Trash2 size={18} className="text-red-600" />
            </div>
            <p className="text-gray-600">
              Are you sure you want to delete{" "}
              <b className="text-gray-800">{selectedBonus?.name}</b>? This action
              cannot be undone.
            </p>
          </div>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowDeleteModal(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
            >
              Delete
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
 
/* ───────── MODAL ───────── */
function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl relative animate-in">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
        >
          <X size={18} />
        </button>
        <h2 className="text-lg font-semibold mb-4 text-gray-800">{title}</h2>
        {children}
      </div>
    </div>
  );
}
 
/* ───────── INPUTS ───────── */
const Input = ({ label, ...props }) => (
  <div>
    <label className="text-sm font-medium text-gray-700">{label}</label>
    <input
      {...props}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 mt-1 outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition"
    />
  </div>
);
 
/* ───────── STATUS ───────── */
function Status({ formData, setFormData }) {
  return (
    <div>
      <label className="text-sm font-medium text-gray-700">Status</label>
      <div className="flex gap-6 mt-2">
        {["active", "inactive"].map((s) => (
          <label
            key={s}
            className={`flex items-center gap-2 cursor-pointer px-3 py-1.5 rounded-lg border transition ${
              formData.status === s
                ? s === "active"
                  ? "border-green-400 bg-green-50"
                  : "border-red-400 bg-red-50"
                : "border-gray-200"
            }`}
          >
            <input
              type="radio"
              checked={formData.status === s}
              onChange={() => setFormData({ ...formData, status: s })}
              className="accent-[#8c57ff]"
            />
            <span
              className={`text-sm capitalize ${
                formData.status === s
                  ? s === "active"
                    ? "text-green-700 font-medium"
                    : "text-red-700 font-medium"
                  : "text-gray-500"
              }`}
            >
              {s}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}
 
/* ───────── ACTIONS ───────── */
const Actions = ({ onClose, loading }) => (
  <div className="flex justify-end gap-3 pt-3">
    <button
      type="button"
      onClick={onClose}
      disabled={loading}
      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
    >
      Cancel
    </button>

    <button
      type="submit"
      disabled={loading}
      className="px-6 py-2 text-white rounded-lg font-medium transition hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
      style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}
    >
      {loading && (
        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
      )}
      {loading ? "Saving..." : "Save"}
    </button>
  </div>
);